import { mapState } from "vuex";
import featureFlagsMixin from "@/mixins/featureFlags";

/**
 * Pre-rebrand logo, served when `feature-performx-logo` is off.
 *
 * Name, support email, favicon, and store URLs are not in this fallback.
 * Those always come from the host's whitelabel config, including
 * app.wewhistle.com (the clientId=0 row, ADO #185887 AC-4).
 *
 * The logo asset mirrors WHISTLE_FAVICONS.favicon's sibling brand mark in
 * utils/brandFavicons.js.
 */
export const WHISTLE_BRANDING = {
  applicationName: "Whistle",
  supportEmail: "help@wewhistle.com",
  applicationLogoUrl: require("@/assets/Logo/whistle-logo-gradient-clean-v2.png"),
  // Bug #189562 (PR review): stays in sync with WHISTLE_FAVICONS.favicon in
  // utils/brandFavicons.js — "/favicon.ico" is now a permanent blank
  // placeholder (see public/index.html), so this default must not point there.
  applicationFaviconUrl: "/whistle-favicon.ico",
  appStoreUrl: process.env.VUE_APP_APPLE_STORE_URL,
  googlePlayStoreUrl: process.env.VUE_APP_GOOGLE_STORE_URL,
};

/**
 * Platform wordmark used when `feature-performx-logo` is on and the config
 * has no `logoUrl` (ADO #190095). Lives here so Login / navBar / other
 * consumers do not `require()` PerformX assets themselves.
 */
export const PERFORMX_LOGO_URL = require("@/assets/Logo/performx-logo-navy.png");

/**
 * Read-only view of the whitelabel config, renaming the two generically-named
 * fields on the way out so consumers get names unlikely to collide with their
 * own locals.
 *
 * applicationName, supportEmail, applicationFaviconUrl, appStoreUrl, and
 * googlePlayStoreUrl always come from the host's whitelabel config. On
 * app.wewhistle.com that is the platform config fetched for that domain.
 * Nulls stay null — a seed-data bug that should be visible rather than
 * silently papered over here.
 *
 * applicationLogoUrl stays gated by `feature-performx-logo`, the same flag
 * that drives the navBar logo. With the flag off it is the bundled Whistle
 * logo. With the flag on, a null/empty `logoUrl` falls back to the bundled
 * PerformX wordmark so consumers never `require()` that asset themselves
 * (ADO #190095 / #190094).
 */
export default {
  mixins: [featureFlagsMixin],
  computed: {
    ...mapState({
      whitelabelConfig: (state) => state.whitelabel,
    }),
    applicationName() {
      return this.resolveWhitelabelField("applicationName");
    },
    supportEmail() {
      return this.resolveWhitelabelField("supportEmail");
    },
    applicationLogoUrl() {
      if (!this.isPerformxLogoEnabled()) return WHISTLE_BRANDING.applicationLogoUrl;
      const url = this.whitelabelConfig.logoUrl;
      if (url == null || url === "") return PERFORMX_LOGO_URL;
      return url;
    },
    applicationFaviconUrl() {
      return this.resolveWhitelabelField("applicationFaviconUrl", "faviconUrl");
    },
    appStoreUrl() {
      return this.resolveWhitelabelField("appStoreUrl");
    },
    googlePlayStoreUrl() {
      return this.resolveWhitelabelField("googlePlayStoreUrl");
    },
    /**
     * True when GET-by-domain resolved the Universal/clientId=0 default
     * (ADO #190097). Not branding — navBar uses this so Rule 1 does not
     * treat the platform default logoUrl as a tenant brand.
     */
    isDefault() {
      return this.whitelabelConfig.isDefault === true;
    },
  },
  methods: {
    /**
     * Always the host's whitelabel config, including app.wewhistle.com.
     * The logo flag does not apply here.
     *
     * @param {string} exposedField - name this mixin exposes to components
     * @param {string} [stateField] - field on state.whitelabel, when it differs
     */
    resolveWhitelabelField(exposedField, stateField = exposedField) {
      return this.whitelabelConfig[stateField];
    },
  },
};
